// Revenue line tabs that carry actual valuation inputs.
// Use this constant (not `Object.keys(DEFAULT_REVENUE_LINE_TERMINOLOGY)`) to iterate tabs —
// the dictionary also carries non-line config (per-line `total_production` + an `expenses`
// section with `direct_expenses` and `benchmark_source`). For `expenses.benchmark_source`,
// `label` is the source NAME (e.g. "Duff & Phelps") and `definition` is the disclosure SENTENCE.
// Mirror of Ruby `Company::REVENUE_LINE_KEYS`.
export const REVENUE_LINE_KEYS = ['wealth', 'life', 'group', 'pc'];

export const DEFAULT_REVENUE_LINE_TERMINOLOGY = {
  wealth: {
    fa_retention_percent: {
      label: 'FA Retention %',
      definition: 'Expected percentage of client relationships or revenue retained annually by the advisor/practice.',
    },
    fee_based_revenue: {
      label: 'Fee-Based Revenue',
      definition: 'Recurring advisory revenue earned from assets under management and ongoing planning relationships.',
    },
    trailer_revenue: {
      label: 'Trailer Revenue',
      definition: 'Ongoing trailing commissions paid on investment or insurance products already in force.',
    },
    direct_revenue: {
      label: 'Direct Revenue',
      definition: 'Revenue paid directly to the practice for advice, planning, or services outside of trailing/product commissions.',
    },
    transactional_revenue: {
      label: 'Transactional Revenue',
      definition: 'One-time revenue generated from product sales, switches, or transaction-based activity.',
    },
    total_production: {
      label: 'Total Production',
      definition: '',
    },
  },
  life: {
    first_year_commissions: {
      label: 'First Year Commissions',
      definition: 'New-business commissions earned in the first year a policy is placed.',
    },
    renewal_commissions: {
      label: 'Renewal Commissions',
      definition: 'Recurring commissions earned on in-force policies after the initial year.',
    },
    override_commissions: {
      label: 'Override Commissions',
      definition: 'Additional commissions earned from supervising or supporting other producers.',
    },
    other_revenues: {
      label: 'Other Revenues',
      definition: 'Any life-line revenue not captured in first-year, renewal, or override commissions.',
    },
    total_production: {
      label: 'Total Production',
      definition: '',
    },
  },
  group: {
    first_year_commissions: {
      label: 'First Year Commissions',
      definition: 'New-business commissions earned in the first year group plans are sold.',
    },
    renewal_commissions: {
      label: 'Renewal Commissions',
      definition: 'Recurring commissions earned on existing group plans after the initial year.',
    },
    override_commissions: {
      label: 'Override Commissions',
      definition: 'Additional commissions earned through team leadership or producer overrides.',
    },
    other_revenues: {
      label: 'Other Revenues',
      definition: 'Any group-line revenue not captured in first-year, renewal, or override commissions.',
    },
    total_production: {
      label: 'Total Production',
      definition: '',
    },
  },
  pc: {
    premium: {
      label: 'Premium',
      definition: 'Annualized written premium used to estimate commission-based production.',
    },
    commission_rate: {
      label: 'Commission Rate',
      definition: 'Average commission percentage earned on premium volume.',
    },
    retention_rate: {
      label: 'Retention Rate',
      definition: 'Percentage of policies or accounts expected to renew year over year.',
    },
    total_production: {
      label: 'Total Production',
      definition: '',
    },
  },
  expenses: {
    direct_expenses: {
      label: 'Direct Expenses',
      definition: '',
    },
    benchmark_source: {
      label: '',
      definition: '',
    },
  },
};

const normalizedStringOrFallback = (value, fallback) => {
  if (value === null || value === undefined) return fallback;
  const next = String(value).trim();
  return next.length > 0 ? next : fallback;
};

export const getRevenueLineTerminology = (customTerminology) => {
  const custom = customTerminology || {};

  const resolved = {};
  Object.keys(DEFAULT_REVENUE_LINE_TERMINOLOGY).forEach((lineKey) => {
    resolved[lineKey] = {};
    const defaultLine = DEFAULT_REVENUE_LINE_TERMINOLOGY[lineKey];
    Object.keys(defaultLine).forEach((fieldKey) => {
      const fallback = defaultLine[fieldKey];
      const incoming = custom?.[lineKey]?.[fieldKey] || {};
      resolved[lineKey][fieldKey] = {
        label: normalizedStringOrFallback(incoming.label, fallback.label),
        definition: normalizedStringOrFallback(incoming.definition, fallback.definition),
      };
    });
  });

  return resolved;
};

export const getRevenueLineTerm = (terminology, lineKey, fieldKey) => {
  const resolved = getRevenueLineTerminology(terminology);
  return resolved?.[lineKey]?.[fieldKey] || { label: '', definition: '' };
};
